import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  IconButton,
  SvgIcon,
  SvgIconProps,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { sidebarConfig, useSidebarPinState } from '@backstage/core-components';
import { useLocation } from 'react-router-dom';

/**
 * How wide the pinned panel is: as wide as a flyout. Backstage does not export
 * the width of its flyouts, this is its default.
 */
const SUBMENU_WIDTH = 202;

const STORAGE_KEY = 'rtl/sidebar/pinned-submenu';

/*
 * The choice in the browser's storage, where there is one. A browser that
 * blocks site storage - by its settings, or in a sandboxed frame - throws on
 * the mere access, and the provider wraps the whole sidebar: an exception
 * here would blank the app. Without storage the menu is pinned, as for
 * someone who never chose, and the choice lasts until the page is reloaded.
 */
function storedPinned(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function storePinned(pinned: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(pinned));
  } catch {
    // Not remembered then, see storedPinned.
  }
}

type PinnedSubmenuContextValue = {
  /** Whether the reader has pinned the flyout. */
  pinned: boolean;
  /** Whether the pinned panel is up: pinned, and on a page of the menu. */
  shown: boolean;
  setPinned: (pinned: boolean) => void;
};

const PinnedSubmenuContext = createContext<PinnedSubmenuContextValue>({
  pinned: false,
  shown: false,
  setPinned: () => {},
});

/** Whether the Catalog flyout is pinned, and the means to change that. */
export const usePinnedSubmenu = () => useContext(PinnedSubmenuContext);

/**
 * Props for {@link PinnedSubmenuProvider}.
 */
type PinnedSubmenuProviderProps = PropsWithChildren<{
  /**
   * Whether the page at this path is one the menu leads to. The panel is only
   * up on those - anywhere else it would take room from a page it has nothing
   * to do with - and comes back by itself on the next one.
   */
  isMenuPage: (pathname: string) => boolean;
}>;

/**
 * Holds whether the flyout is pinned, remembered across visits in the
 * browser like Backstage remembers a pinned sidebar.
 *
 * Pinned until the reader unpins it: the menu is how the catalog is found
 * around in, and a reader who would rather have the room says so once.
 */
export const PinnedSubmenuProvider = (props: PinnedSubmenuProviderProps) => {
  const { isMenuPage } = props;
  const { pathname } = useLocation();
  const [pinned, setPinned] = useState(storedPinned);

  useEffect(() => storePinned(pinned), [pinned]);

  const shown = pinned && isMenuPage(pathname);

  return (
    <PinnedSubmenuContext.Provider value={{ pinned, shown, setPinned }}>
      {props.children}
    </PinnedSubmenuContext.Provider>
  );
};

/** Material's push pin, which the icons of MUI v4 do not have. */
const PinIcon = (props: SvgIconProps & { pinned: boolean }) => {
  const { pinned, ...iconProps } = props;
  return (
    <SvgIcon {...iconProps}>
      {pinned ? (
        <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" />
      ) : (
        <path d="M14 4v5c0 1.12.37 2.16 1 3H9c.65-.86 1-1.9 1-3V4h4m3-2H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3V4h1c.55 0 1-.45 1-1s-.45-1-1-1z" />
      )}
    </SvgIcon>
  );
};

const useStyles = makeStyles(theme => ({
  pinButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(1),
    color: theme.palette.navigation.color,
  },
  panel: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    width: SUBMENU_WIDTH,
    // Under the sidebar, which expands over it on hover like over the page.
    zIndex: 999,
    display: 'flex',
    flexFlow: 'column nowrap',
    background: theme.palette.navigation.submenu?.background ?? '#404040',
    overflowX: 'hidden',
    overflowY: 'auto',
    transition: theme.transitions.create('left'),
    '& > *': {
      flexShrink: 0,
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  // The same as the title of a flyout, see `SidebarSubmenu`.
  title: {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium as number,
    color: theme.palette.navigation.color,
    padding: theme.spacing(2.5),
  },
  content: {
    transition: theme.transitions.create('padding-left'),
  },
}));

/**
 * Pins the flyout it sits in, or unpins the panel it sits in. Placed in the
 * top right corner, next to the title.
 */
export const PinSubmenuButton = () => {
  const classes = useStyles();
  const { pinned, setPinned } = usePinnedSubmenu();
  return (
    <Tooltip title={pinned ? 'Unpin' : 'Pin'}>
      <IconButton
        size="small"
        className={classes.pinButton}
        aria-label={pinned ? 'Unpin menu' : 'Pin menu'}
        onClick={() => setPinned(!pinned)}
      >
        <PinIcon pinned={pinned} fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * The flyout, pinned: the same entries in a panel that stays open next to the
 * sidebar until it is unpinned.
 *
 * Backstage keeps whether a flyout is open to itself, so a flyout cannot be
 * held open; this stands in for it, looking the same. Not on a phone, where
 * there is no room beside the sidebar.
 */
export const PinnedSubmenuPanel = (
  props: PropsWithChildren<{ title: string }>,
) => {
  const classes = useStyles();
  const { shown } = usePinnedSubmenu();
  const { isPinned: sidebarPinned, isMobile } = useSidebarPinState();

  if (!shown || isMobile) {
    return null;
  }
  return (
    <nav
      className={classes.panel}
      style={{
        left: sidebarPinned
          ? sidebarConfig.drawerWidthOpen
          : sidebarConfig.drawerWidthClosed,
      }}
      aria-label={props.title}
    >
      <Typography variant="h5" component="span" className={classes.title}>
        {props.title}
      </Typography>
      <PinSubmenuButton />
      {props.children}
    </nav>
  );
};

/**
 * The page next to the sidebar, moved over by the pinned panel, which
 * `SidebarPage` does not know to make room for.
 */
export const SidebarContent = (props: PropsWithChildren<{}>) => {
  const classes = useStyles();
  const { shown } = usePinnedSubmenu();
  const { isMobile } = useSidebarPinState();

  return (
    <div
      className={classes.content}
      style={{ paddingLeft: shown && !isMobile ? SUBMENU_WIDTH : 0 }}
    >
      {props.children}
    </div>
  );
};
